import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Plus, PencilSimple, Trash, X } from "@phosphor-icons/react";

const EMPTY = { title: "", slug: "", excerpt: "", cover_image: "", body_html: "", tags: "", published: true };

export default function BlogAdmin() {
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  const load = () => api.get("/admin/posts").then(r => setPosts(r.data));
  useEffect(() => { load(); }, []);

  const startNew = () => { setEditing("new"); setForm(EMPTY); setMsg(""); };
  const startEdit = (p) => {
    setEditing(p.id);
    setForm({ ...p, tags: (p.tags || []).join(", ") });
    setMsg("");
  };
  const cancel = () => { setEditing(null); setForm(EMPTY); };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true); setMsg("");
    try {
      const payload = {
        title: form.title,
        slug: form.slug || undefined,
        excerpt: form.excerpt,
        cover_image: form.cover_image || null,
        body_html: form.body_html,
        tags: form.tags.split(",").map(s => s.trim()).filter(Boolean),
        published: !!form.published,
      };
      if (editing === "new") {
        await api.post("/admin/posts", payload);
        setMsg("Post created.");
      } else {
        await api.patch(`/admin/posts/${editing}`, payload);
        setMsg("Post updated.");
      }
      await load();
      cancel();
    } catch (e) {
      setMsg("Save failed: " + (e.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this post?")) return;
    await api.delete(`/admin/posts/${id}`);
    load();
  };

  return (
    <div className="border-t border-zinc-900 px-6 md:px-12 py-8">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl font-black tracking-tighter uppercase font-[Outfit]">Blog Posts</h2>
        {!editing && (
          <button data-testid="blog-new-btn" onClick={startNew} className="btn-brutal btn-red">
            <Plus size={14} weight="bold" className="mr-2" /> New Post
          </button>
        )}
      </div>
      {msg && <div className="border border-zinc-900 bg-yellow-100 px-4 py-2 text-xs uppercase tracking-widest font-bold font-[Outfit] mb-4">{msg}</div>}

      {editing && (
        <form onSubmit={save} className="border border-zinc-900 p-6 mb-6 space-y-4 bg-zinc-50">
          <div className="flex justify-between items-center">
            <div className="font-[Outfit] font-bold uppercase tracking-tighter text-lg">{editing === "new" ? "New Post" : "Edit Post"}</div>
            <button type="button" onClick={cancel} className="p-1 hover:bg-zinc-900 hover:text-white"><X size={16} /></button>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest font-bold font-[Outfit] block mb-1">Title *</label>
            <input data-testid="blog-title" required className="input-brutal" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs uppercase tracking-widest font-bold font-[Outfit] block mb-1">Slug (optional)</label>
              <input className="input-brutal" placeholder="auto from title" value={form.slug} onChange={e => setForm({ ...form, slug: e.target.value })} />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest font-bold font-[Outfit] block mb-1">Tags (comma-separated)</label>
              <input className="input-brutal" value={form.tags} onChange={e => setForm({ ...form, tags: e.target.value })} />
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest font-bold font-[Outfit] block mb-1">Cover Image URL</label>
            <input className="input-brutal" value={form.cover_image} onChange={e => setForm({ ...form, cover_image: e.target.value })} />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest font-bold font-[Outfit] block mb-1">Excerpt *</label>
            <textarea required rows={2} className="input-brutal" value={form.excerpt} onChange={e => setForm({ ...form, excerpt: e.target.value })} />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest font-bold font-[Outfit] block mb-1">Body (HTML) *</label>
            <textarea data-testid="blog-body" required rows={14} className="input-brutal font-mono text-xs" value={form.body_html} onChange={e => setForm({ ...form, body_html: e.target.value })} placeholder="<p>Use HTML tags: &lt;h2&gt;, &lt;p&gt;, &lt;ul&gt;&lt;li&gt;, &lt;a&gt;, &lt;strong&gt;…</p>" />
          </div>
          <label className="flex items-center gap-2 text-sm font-[Outfit] font-bold uppercase tracking-widest">
            <input type="checkbox" checked={form.published} onChange={e => setForm({ ...form, published: e.target.checked })} /> Published
          </label>
          <button data-testid="blog-save-btn" disabled={saving} className="btn-brutal btn-red">{saving ? "Saving…" : "Save Post"}</button>
        </form>
      )}

      <div className="border border-zinc-900">
        {posts.length === 0 ? (
          <div className="p-6 text-center text-sm text-zinc-500">No posts yet.</div>
        ) : posts.map(p => (
          <div key={p.id} className="flex items-center gap-4 p-4 border-b border-zinc-200 last:border-b-0">
            <div className="w-16 h-16 bg-zinc-100 border border-zinc-900 flex-shrink-0 overflow-hidden">
              {p.cover_image && <img src={p.cover_image} alt="" className="w-full h-full object-cover" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-bold leading-tight">{p.title}</div>
              <div className="text-xs text-zinc-500 font-mono mt-1">/blog/{p.slug}</div>
            </div>
            <span className={`tag ${p.published ? "bg-[#0055ff] text-white border-[#0055ff]" : ""}`}>{p.published ? "Live" : "Draft"}</span>
            <button onClick={() => startEdit(p)} className="w-9 h-9 border border-zinc-900 flex items-center justify-center hover:bg-zinc-900 hover:text-white"><PencilSimple size={14} weight="bold" /></button>
            <button onClick={() => remove(p.id)} className="w-9 h-9 border border-zinc-900 flex items-center justify-center hover:bg-[#ff3333] hover:border-[#ff3333] hover:text-white"><Trash size={14} weight="bold" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}
